import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowLeft, History, Loader2, LogIn, Palette, Pencil, RefreshCw, Save, X } from 'lucide-react';
import { waysApi, type DesignStandard } from '../services/waysApi';
import { designStandardApi, ApiError, type DesignStandardVersion } from '../services/api';
import { signIn, WaysSignInRequired } from '../services/waysAuth';
import { useAuthStore } from '../store/useAuthStore';
import WaysSlideover from '../components/ways/WaysSlideover';

/**
 * The design standard, on the Ways rail (hs-pmo/ep43#12; Zane, 2026-10-26): the standard every
 * agent builds to -- its principles, tokens, components, patterns and checks -- with its
 * versions, and an editor for its owner.
 *
 * The harness holds it (through PMO's proxy, /api/ways/design-standard). Until the harness
 * store is live (hs-harness/ep1#22) the page shows what Ways serves -- the seed it ships with
 * -- and says so; Versions and Edit stay off with the reason, since there is nowhere to save.
 * Edit is the owner's alone (the server decides: a super_admin the standard names as owner).
 *
 * Two read sources, on purpose: PMO's proxy to the harness first, because only it says whether
 * this person may edit and which version an edit is based on; then GET
 * https://ways.hsinc.ai/api/standards/design, the copy agents read (the harness's once Ways is
 * connected to it, the seed until then). The page is itself built to the standard: the theme's ramp,
 * Headless UI dialogs, visible focus, bound labels, skeleton loading, gated controls with the
 * reason inline, and no personal data it does not need.
 */

type Tab = 'principles' | 'tokens' | 'components' | 'patterns' | 'checks';
const TABS: { id: Tab; label: string }[] = [
  { id: 'principles', label: 'Principles' },
  { id: 'tokens', label: 'Tokens' },
  { id: 'components', label: 'Components' },
  { id: 'patterns', label: 'Patterns' },
  { id: 'checks', label: 'Checks' },
];

const SEVERITY: Record<string, string> = {
  blocker: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  major: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  advisory: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

function Severity({ s }: { s?: string }) {
  if (!s) return null;
  return <span className={clsx('px-1.5 rounded text-[11px] font-medium', SEVERITY[s] || SEVERITY.advisory)}>{s}</span>;
}

interface Shown {
  source: string;
  version?: string;
  standard: DesignStandard;
  /** Set when an older version is open from the history. */
  historic?: boolean;
}

/** Is this a {light, dark} colour pair as the token file writes them? */
function isPair(v: unknown): v is { light: string; dark: string } {
  return !!v && typeof v === 'object' && typeof (v as Record<string, unknown>).light === 'string'
    && typeof (v as Record<string, unknown>).dark === 'string';
}

function Swatch({ color }: { color: string }) {
  // Token values are data from the standard: shown as a swatch only when they look like a colour.
  const ok = /^#[0-9a-f]{3,8}$/i.test(color) || /^(rgb|hsl)a?\(/i.test(color);
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs text-gray-600 dark:text-gray-300">
      {ok ? <span className="w-4 h-4 rounded border border-gray-300 dark:border-gray-600" style={{ background: color }} /> : null}
      {color}
    </span>
  );
}

/** Any token group: colour pairs as swatches, nested groups as rows, the rest as text. */
function TokenValue({ value }: { value: unknown }) {
  if (isPair(value)) return <span className="flex gap-4"><Swatch color={value.light} /><Swatch color={value.dark} /></span>;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const s = String(value);
    return /^#[0-9a-f]{3,8}$/i.test(s) ? <Swatch color={s} /> : <span className="text-sm text-gray-700 dark:text-gray-200 break-words">{s}</span>;
  }
  if (Array.isArray(value)) {
    return <span className="text-sm text-gray-700 dark:text-gray-200 break-words">{value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ')}</span>;
  }
  if (value && typeof value === 'object') {
    return (
      <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-1">
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-xs font-mono text-gray-500 pt-0.5">{k}</dt>
            <dd><TokenValue value={v} /></dd>
          </div>
        ))}
      </dl>
    );
  }
  return null;
}

/** The checks the owner's draft must pass before it is sent; the server checks again. */
function draftProblem(text: string): string | null {
  let doc: unknown;
  try { doc = JSON.parse(text); } catch (e) { return `Not valid JSON: ${(e as Error).message}`; }
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return 'The standard must be a JSON object.';
  const ps = (doc as DesignStandard).principles;
  if (!Array.isArray(ps) || !ps.length) return 'The standard needs a non-empty principles list.';
  const ids = ps.map((p) => p?.id);
  if (ids.some((i) => !i)) return 'Every principle needs an id.';
  if (new Set(ids).size !== ids.length) return 'Principle ids must be unique.';
  if (ps.some((p) => !p.statement)) return 'Every principle needs a statement.';
  return null;
}

export default function WaysDesignStandardPage() {
  const { user } = useAuthStore();
  const [shown, setShown] = useState<Shown | null>(null);
  const [harness, setHarness] = useState<{ available: boolean; reason?: string; canEdit: boolean; editRefusal?: string | null; version?: string }>(
    { available: false, canEdit: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needWays, setNeedWays] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('principles');
  const [filter, setFilter] = useState('');

  // The Versions slideout has its own address (`?versions=1`), as the standard's nav-slideouts
  // asks of every slideout state: it can be linked, and Back closes it.
  const [params, setParams] = useSearchParams();
  const historyOpen = params.get('versions') === '1';
  const setHistoryOpen = (open: boolean) => {
    const next = new URLSearchParams(params);
    if (open) next.set('versions', '1'); else next.delete('versions');
    setParams(next);
  };
  const [versions, setVersions] = useState<DesignStandardVersion[] | null>(null);
  const [versionsReason, setVersionsReason] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The harness first: it is where the standard lives, and it says whether this person may edit.
      const h = await designStandardApi.current().catch((e) => ({ available: false as const, reason: e instanceof Error ? e.message : String(e) }));
      if (h.available) {
        setHarness({ available: true, canEdit: h.canEdit, editRefusal: h.editRefusal, version: h.version });
        setShown({ source: 'harness', version: h.version, standard: h.standard });
        return;
      }
      setHarness({ available: false, reason: h.reason, canEdit: false });
      // Not live yet: what Ways serves (the seed), read with the person's Ways session.
      try {
        const w = await waysApi.designStandard();
        setShown({ source: w.source, version: w.version ?? w.standard.version, standard: w.standard });
        setNeedWays(null);
      } catch (e) {
        if (e instanceof WaysSignInRequired) setNeedWays(e.message || '');
        else throw e;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Read whenever the slideout opens, from the button or from its address.
  useEffect(() => {
    if (!historyOpen) return;
    let alive = true;
    setVersions(null);
    designStandardApi.versions().then((r) => {
      if (!alive) return;
      setVersions(r.versions || []);
      setVersionsReason(r.available ? null : (r.reason || 'The harness does not keep versions yet.'));
    }).catch((e) => {
      if (!alive) return;
      setVersions([]);
      setVersionsReason(e instanceof Error ? e.message : String(e));
    });
    return () => { alive = false; };
  }, [historyOpen]);
  const openHistory = () => setHistoryOpen(true);

  const openVersion = async (v: string) => {
    try {
      const r = await designStandardApi.version(v);
      setShown({ source: 'harness', version: r.version, standard: r.standard, historic: v !== harness.version });
      setHistoryOpen(false);
      setEditing(false);
    } catch (e) {
      setVersionsReason(e instanceof Error ? e.message : String(e));
    }
  };

  const startEdit = () => {
    if (!shown) return;
    setDraft(JSON.stringify(shown.standard, null, 2));
    setNote('');
    setSaveError(null);
    setEditing(true);
  };

  const problem = useMemo(() => (editing ? draftProblem(draft) : null), [editing, draft]);

  const save = async () => {
    if (problem || !note.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await designStandardApi.save(JSON.parse(draft) as DesignStandard, harness.version, note.trim());
      setEditing(false);
      await load();
    } catch (e) {
      setSaveError(e instanceof ApiError && e.status === 409
        ? 'Someone saved a newer version since you opened the editor. Reload, then make your change again.'
        : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const editOff = !harness.available
    ? 'Editing needs the harness design-standard store, which is not live yet (hs-harness/ep1#22).'
    : !harness.canEdit ? (harness.editRefusal || 'Only the standard\'s owner may edit it.')
      : shown?.historic ? 'Open the current version to edit.' : null;

  const std = shown?.standard;
  const principles = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const list = (std?.principles || []).filter((p) => !needle
      || `${p.id} ${p.group || ''} ${p.statement} ${p.why || ''}`.toLowerCase().includes(needle));
    const by: Record<string, typeof list> = {};
    for (const p of list) (by[p.group || 'other'] ||= []).push(p);
    return Object.entries(by);
  }, [std, filter]);

  if (needWays !== null && !shown) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-center">
          <Palette className="w-8 h-8 mx-auto mb-3 text-accent" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Sign in to Ways</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            {needWays || 'Until the harness store is live, the standard is read from Ways, which uses its own sign-in.'}
          </p>
          <button type="button" onClick={() => signIn(user?.email, '/ways/design-standard')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            <LogIn className="w-4 h-4" /> Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 h-full flex flex-col bg-white dark:bg-gray-900">
      <header className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-2">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-gray-500">
            <Link to="/ways" className="inline-flex items-center gap-1 rounded-lg px-1 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Ways
            </Link>
            <span aria-hidden="true">/</span>
          </nav>
          <Palette className="w-4 h-4 text-accent" aria-hidden="true" />
          <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Design Standard</h1>
          {shown ? (
            <>
              <span className="px-1.5 rounded bg-gray-100 dark:bg-gray-700 text-xs font-mono text-gray-700 dark:text-gray-200">v{shown.version || '?'}</span>
              <span className={clsx('px-1.5 rounded text-xs',
                shown.source === 'harness' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300')}
                title={shown.source === 'harness' ? 'Read from the harness, where the standard is kept'
                  : 'The seed Ways ships with: the harness store is not live yet'}>
                {shown.source === 'harness' ? 'harness' : 'seed (harness not live)'}
              </span>
            </>
          ) : null}
          <div className="ml-auto flex items-center gap-1.5">
            <button type="button" onClick={load} disabled={loading} title="Reload" aria-label="Reload the standard"
              className="p-1.5 rounded-lg text-gray-500 hover:text-accent disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <RefreshCw className={clsx('w-3.5 h-3.5', loading && 'motion-safe:animate-spin')} aria-hidden="true" />
            </button>
            <button type="button" onClick={openHistory}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <History className="w-3.5 h-3.5" /> Versions
            </button>
            <button type="button" onClick={startEdit} disabled={!!editOff || !shown || editing}
              title={editOff || 'Edit the standard: saved as a new version in the harness'}
              aria-describedby={editOff ? 'ds-edit-gate' : undefined}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          </div>
        </div>
        {std ? (
          <p className="mt-1 text-xs text-gray-500">
            What every agent and app builds to. {std.status ? <>Status {std.status} · </> : null}
            {std.asOf ? <>as of {std.asOf} · </> : null}
            {std.principles.length} principles
            {std.appliesTo?.length ? <> · applies to {std.appliesTo.join(', ')}</> : null}
          </p>
        ) : null}
        {editOff && !editing && shown ? (
          <p id="ds-edit-gate" className="mt-1 text-xs text-gray-500">
            Edit is off: {editOff}{!harness.available && harness.reason ? ` (${harness.reason})` : ''}
          </p>
        ) : null}
        {shown?.historic ? (
          <div className="mt-2 flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300">
            Viewing version {shown.version}, not the current one.
            <button type="button" onClick={load} className="underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">Back to current</button>
          </div>
        ) : null}
      </header>

      {error ? (
        <div role="alert" className="px-4 py-2 text-sm text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 border-b border-red-200 dark:border-red-800">{error}</div>
      ) : null}

      {loading && !shown ? (
        <div className="p-4 space-y-3" aria-busy="true">
          <span className="sr-only">Loading…</span>
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 max-w-3xl rounded-lg bg-gray-100 dark:bg-gray-800 motion-safe:animate-pulse" />)}
        </div>
      ) : editing ? (
        <div className="flex-1 min-h-0 flex flex-col p-4 gap-2">
          <p className="text-xs text-gray-500">
            The whole standard as JSON. Saving makes a new version in the harness; agents and the governor pick it up within five minutes.
          </p>
          <label htmlFor="ds-draft" className="sr-only">The standard as JSON</label>
          <textarea id="ds-draft" value={draft} onChange={(e) => setDraft(e.target.value)} spellCheck={false}
            aria-invalid={!!problem} aria-describedby={problem ? 'ds-draft-error' : undefined}
            className="flex-1 min-h-80 font-mono text-xs p-3 border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent" />
          {problem ? <p id="ds-draft-error" role="alert" className="text-sm text-red-600 dark:text-red-400">{problem}</p> : null}
          <label htmlFor="ds-note" className="text-sm text-gray-700 dark:text-gray-200">
            What changed <span className="text-gray-500">(kept with the version; required)</span>
          </label>
          <input id="ds-note" value={note} aria-describedby={saveError ? 'ds-save-error' : undefined} onChange={(e) => setNote(e.target.value)} maxLength={500}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent" />
          {saveError ? <p id="ds-save-error" role="alert" className="text-sm text-red-600 dark:text-red-400">{saveError}</p> : null}
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving || !!problem || !note.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent text-white text-sm font-medium disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              {saving ? <Loader2 className="w-3.5 h-3.5 motion-safe:animate-spin" aria-hidden="true" /> : <Save className="w-3.5 h-3.5" />} Save as a new version
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      ) : std ? (
        <>
          <div role="tablist" className="flex gap-4 px-4 border-b border-gray-200 dark:border-gray-700 text-sm">
            {TABS.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
                className={clsx('py-2 -mb-px border-b-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent', tab === t.id ? 'border-accent text-accent font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200')}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {tab === 'principles' ? (
              <>
                <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter principles"
                  aria-label="Filter principles"
                  className="mb-3 w-full max-w-sm px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent" />
                {std.baseline ? <p className="mb-4 text-xs text-gray-500 max-w-3xl">{std.baseline}</p> : null}
                {principles.map(([group, list]) => (
                  <section key={group} className="mb-5">
                    <h2 className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">{group} · {list.length}</h2>
                    <ul className="divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                      {list.map((p) => (
                        <li key={p.id} className="px-3 py-2.5">
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-mono text-gray-600 dark:text-gray-300">{p.id}</span>
                            <Severity s={p.severity} />
                            {p.verify ? <span className="text-gray-400" title={std.verification?.[p.verify]}>verified by {p.verify}</span> : null}
                            {p.baseline ? <span className="text-gray-400">baseline</span> : null}
                          </div>
                          <p className="mt-1 text-sm text-gray-800 dark:text-gray-100">{p.statement}</p>
                          {p.why ? <p className="mt-0.5 text-xs text-gray-500">{p.why}</p> : null}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                {!principles.length ? <p className="text-sm text-gray-500">No principle matches.</p> : null}
                {std.severities ? (
                  <section className="mt-6 max-w-3xl">
                    <h2 className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">Severities</h2>
                    <dl className="space-y-1">
                      {Object.entries(std.severities).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-sm"><dt className="shrink-0"><Severity s={k} /></dt><dd className="text-gray-600 dark:text-gray-300">{v}</dd></div>
                      ))}
                    </dl>
                  </section>
                ) : null}
              </>
            ) : tab === 'tokens' ? (
              std.tokens ? (
                <div className="space-y-5 max-w-4xl">
                  {typeof std.tokens.rule === 'string' ? <p className="text-sm text-gray-700 dark:text-gray-200">{std.tokens.rule}</p> : null}
                  {typeof std.tokens.source === 'string' ? <p className="text-xs text-gray-500">From {std.tokens.source}</p> : null}
                  {Object.entries(std.tokens).filter(([k]) => k !== 'rule' && k !== 'source').map(([k, v]) => (
                    <section key={k}>
                      <h2 className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">
                        {k}{isPairGroup(v) ? ' · light / dark' : ''}
                      </h2>
                      <TokenValue value={v} />
                    </section>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-500">This version has no tokens.</p>
            ) : tab === 'checks' ? (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg max-w-4xl">
                {(std.checks || []).map((c) => (
                  <li key={c.id} className="px-3 py-2.5">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-mono text-gray-600 dark:text-gray-300">{c.id}</span>
                      {c.kind ? <span className="px-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{c.kind}</span> : null}
                    </div>
                    {c.threshold ? <p className="mt-1 text-sm text-gray-800 dark:text-gray-100">{c.threshold}</p> : null}
                    <p className="mt-0.5 text-xs text-gray-500">
                      {c.covers?.length ? <>Covers {c.covers.join(', ')}. </> : null}
                      {c.from ? <>From {c.from}.</> : null}
                    </p>
                  </li>
                ))}
                {!std.checks?.length ? <li className="px-3 py-4 text-sm text-gray-500">No checks in this version.</li> : null}
              </ul>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg max-w-4xl">
                {((tab === 'components' ? std.components : std.patterns) || []).map((r) => (
                  <li key={r.id} className="px-3 py-2.5">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-gray-600 dark:text-gray-300">{r.id}</span>
                      <Severity s={r.severity} />
                    </div>
                    {r.rule ? <p className="mt-1 text-sm text-gray-800 dark:text-gray-100">{r.rule}</p> : null}
                  </li>
                ))}
                {!((tab === 'components' ? std.components : std.patterns) || []).length ? (
                  <li className="px-3 py-4 text-sm text-gray-500">None in this version.</li>
                ) : null}
              </ul>
            )}
          </div>
        </>
      ) : null}

      <WaysSlideover open={historyOpen} title="Versions" icon={<History className="w-4 h-4 text-accent" />}
        width="w-[420px]" onClose={() => setHistoryOpen(false)}>
        <div className="flex-1 overflow-y-auto">
          {versions === null ? (
            <div className="px-4 py-3 space-y-2" aria-busy="true">
              <span className="sr-only">Loading…</span>
              {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-lg bg-gray-100 dark:bg-gray-800 motion-safe:animate-pulse" />)}
            </div>
          ) : (
            <>
              {versionsReason ? (
                <p className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                  Versions are kept by the harness, and its design-standard store is not live yet (hs-harness/ep1#22).
                  <span className="block mt-1 text-xs text-gray-400">{versionsReason}</span>
                </p>
              ) : null}
              {versions.length ? (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {versions.map((v) => (
                    <li key={v.version}>
                      <button type="button" onClick={() => openVersion(v.version)}
                        className={clsx('w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                          shown?.version === v.version && 'bg-accent/10')}>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-mono text-gray-800 dark:text-gray-100">v{v.version}</span>
                          {v.version === harness.version ? <span className="text-xs text-emerald-700 dark:text-emerald-400">current</span> : null}
                          {v.savedAt ? <span className="ml-auto text-xs text-gray-400">{new Date(v.savedAt).toLocaleString()}</span> : null}
                        </div>
                        {v.note ? <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{v.note}</p> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : !versionsReason ? (
                <p className="px-4 py-10 text-center text-sm text-gray-500">Each save records a version here, with what changed. None yet.</p>
              ) : null}
            </>
          )}
        </div>
      </WaysSlideover>
    </div>
  );
}

/** A token group made only of {light, dark} pairs (the colour ramp). */
function isPairGroup(v: unknown): boolean {
  return !!v && typeof v === 'object' && !Array.isArray(v)
    && Object.values(v as Record<string, unknown>).length > 0 && Object.values(v as Record<string, unknown>).every(isPair);
}
